import React, { FC } from "react";
import {
  Divider,
  IconButton,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import _ from "lodash";
import { Link } from "react-router-dom";

import {
  StyledAppBar,
  StyledDrawer,
  StyledDrawerHeader,
  StyledIconButton,
  StyledSidebar,
} from "./Sidebar.styled";
import SidebarSection from "./components/SidebarSection";
import SidebarLogo from "./components/SidebarLogo/SidebarLogo";
import SidebarCompanySelect from "./components/SidebarCompanySelect";
import SidebarAccount from "./components/SidebarAccount";
import { getSidebarSections } from "./Sidebar.helpers";

import { useSidebarStateStore } from "@store/sidebar";
import { Box, Grid, Stack } from "@components/base";
import { useGetCurrentCompanyInfo } from "@api";
import { useUserStore } from "@store/user";

interface Sidebar {
  collapsed: boolean;
  onToggleSidebar: () => void;
  currentPath: string;
}

const Sidebar: FC<Sidebar> = ({ collapsed, onToggleSidebar, currentPath }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mobileDrawerOpen, setMobileDrawerOpen } = useSidebarStateStore();
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);

  const sections = getSidebarSections(
    currentCompanyInfo?.companyFeatureFunctionalities,
  );

  const SidebarContent = (
    <Stack height="100%" direction="column">
      {!isMobile && (
        <Box
          position="fixed"
          left={collapsed ? 74 : 280}
          top={60}
          sx={{
            transition: "all 0.5s",
          }}
        >
          <StyledIconButton onClick={onToggleSidebar}>
            <FeatherIcon
              icon={collapsed ? "chevron-right" : "chevron-left"}
              size={16}
            />
          </StyledIconButton>
        </Box>
      )}
      <Box>
        <StyledDrawerHeader
          sx={({ spacing }) => ({
            padding: spacing(3),
            paddingLeft: collapsed ? spacing(2) : undefined,
            paddingRight: collapsed ? spacing(2) : undefined,
          })}
        >
          <Link to="/">
            <SidebarLogo />
          </Link>
        </StyledDrawerHeader>
        <Box sx={{ px: collapsed ? 1 : 2, transition: "padding 0.5s" }}>
          <SidebarCompanySelect />
        </Box>
      </Box>
      <Grid
        container
        alignContent="space-between"
        flexDirection={"column"}
        minHeight="calc(100vh - 218px)"
      >
        <Grid size={12}>
          <SidebarSection items={sections.top} currentPath={currentPath} />
        </Grid>
        <Grid
          justifyContent={"flex-start"}
          flex={1}
          alignContent={"flex-start"}
          size={12}
        >
          {!_.isEmpty(sections.middle) &&
            sections.middle.some((section) => section.isVisible) && (
              <>
                <Divider
                  sx={{
                    margin: "0px 16px",
                    borderColor: "#FFFFFF40",
                  }}
                />
                <SidebarSection items={sections.middle} currentPath={currentPath} />
              </>
            )}
        </Grid>

        <Grid size={12}>
          <SidebarSection items={sections.bottom} currentPath={currentPath} />
          <Divider
            sx={{
              borderColor: "#FFFFFF40",
            }}
          />
          <SidebarAccount />
        </Grid>
      </Grid>
    </Stack>
  );

  const MobileAppBar = (
    <StyledAppBar position="static">
      <Toolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <img src="/static/logo/logo-mobile.svg" height={24} />
          <IconButton
            onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
            color="secondary"
          >
            {!mobileDrawerOpen && <FeatherIcon icon="menu" size={20} />}
            {mobileDrawerOpen && <FeatherIcon icon="x" size={20} />}
          </IconButton>
        </Stack>
      </Toolbar>
    </StyledAppBar>
  );

  return isMobile ? (
    <>
      {MobileAppBar}
      <StyledDrawer open={mobileDrawerOpen} anchor="top">
        {MobileAppBar}
        {SidebarContent}
      </StyledDrawer>
    </>
  ) : (
    <StyledSidebar
      sx={{
        width: collapsed ? 74 : 280,
      }}
    >
      {SidebarContent}
    </StyledSidebar>
  );
};

export default Sidebar;
